import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LogOut, Search, Copy, CheckCircle2, XCircle, Truck, RefreshCw, Phone, MapPin } from "lucide-react";
import { toast } from "sonner";
import { adminApi, formatErr, useAdmin } from "@/context/AdminAuth";
import { Logo } from "@/components/site/Header";
import { inr } from "@/lib/content";

const TABS = [["placed", "Needs verification"], ["confirmed", "Confirmed"], ["shipped", "Shipped"], ["pending_payment", "Awaiting payment"], ["payment_failed", "Payment not received"], ["expired", "Expired"], ["", "All"]];
const TONE = { placed: "bg-[#FFF4DC] text-[#8A5A00]", confirmed: "bg-[#E3F1E8] text-[#1B4332]", shipped: "bg-[#1B4332] text-white", pending_payment: "bg-[#F4EFEA] text-[#57534E]", payment_failed: "bg-red-100 text-red-700", expired: "bg-[#F4EFEA] text-[#A8A29E]" };
const LABEL = Object.fromEntries(TABS);
const ago = (iso) => {
  const m = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  return m < 1 ? "just now" : m < 60 ? `${m} min ago` : m < 1440 ? `${Math.round(m / 60)} h ago` : `${Math.round(m / 1440)} d ago`;
};

const OrderCard = ({ o, onAction, busy }) => (
  <div data-testid={`admin-order-${o.order_number}`} className="rounded-2xl border border-[#E7E2DB] bg-white p-5">
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <p className="font-semibold">{o.order_number} <span className={`ml-2 rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${TONE[o.status]}`}>{LABEL[o.status]}</span></p>
        <p className="mt-1 text-xs text-[#78716C]">Placed {ago(o.created_at)}{o.payment_submitted_at && ` · UTR sent ${ago(o.payment_submitted_at)}`}</p>
      </div>
      <p className="text-right"><span className="block text-xl font-semibold">{inr(o.total)}</span><span className="text-[11px] text-[#78716C]">to receive via UPI</span></p>
    </div>
    {o.utr && (
      <button data-testid={`admin-utr-${o.order_number}`} onClick={() => { navigator.clipboard?.writeText(o.utr); toast.success("UTR copied"); }} className="mt-4 flex w-full items-center justify-between rounded-xl border border-dashed border-[#B85D43] bg-[#F4E4DE] px-4 py-3 text-left">
        <span><span className="block text-[10px] uppercase tracking-[0.18em] text-[#B85D43]">UTR / UPI Ref No.</span><span className="font-mono text-lg font-semibold tracking-wider">{o.utr}</span></span>
        <Copy className="h-4 w-4 text-[#701A24]" />
      </button>
    )}
    <div className="mt-4 grid gap-4 text-sm sm:grid-cols-2">
      <div className="space-y-1">
        <p className="font-medium">{o.customer.name}</p>
        <a href={`tel:${o.customer.phone}`} className="flex items-center gap-1.5 text-[#57534E]"><Phone className="h-3.5 w-3.5" /> {o.customer.phone}</a>
        <p className="flex gap-1.5 text-[#57534E]"><MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {o.customer.address}, {o.customer.city}, {o.customer.state} – {o.customer.pincode}</p>
      </div>
      <ul className="space-y-1.5">
        {o.items.map((i, k) => <li key={k} className="flex justify-between gap-2 text-[#44403C]"><span className="line-clamp-1">{i.qty} × {i.name} <span className="text-[#A8A29E]">({i.size})</span></span><span>{inr(i.line_total)}</span></li>)}
        {o.combo_discount > 0 && <li className="flex justify-between text-[#1B4332]"><span>Combo</span><span>− {inr(o.combo_discount)}</span></li>}
      </ul>
    </div>
    {o.status === "placed" && (
      <div className="mt-5 flex flex-wrap gap-2">
        <button data-testid={`admin-confirm-${o.order_number}`} disabled={busy} onClick={() => onAction(o, "confirm")} className="btn-primary bg-[#1B4332] px-5 py-2.5 hover:bg-[#143526]"><CheckCircle2 className="h-4 w-4" /> Payment received – Confirm</button>
        <button data-testid={`admin-reject-${o.order_number}`} disabled={busy} onClick={() => window.confirm(`Mark ${o.order_number} as payment NOT received? Stock will be released.`) && onAction(o, "reject")} className="inline-flex items-center gap-2 rounded-full border border-red-300 px-5 py-2.5 text-sm font-semibold text-red-700 hover:bg-red-50"><XCircle className="h-4 w-4" /> Not received</button>
      </div>
    )}
    {o.status === "confirmed" && (
      <button data-testid={`admin-ship-${o.order_number}`} disabled={busy} onClick={() => onAction(o, "ship")} className="btn-primary mt-5 px-5 py-2.5"><Truck className="h-4 w-4" /> Mark as shipped</button>
    )}
  </div>
);

export default function AdminDashboard() {
  const { user, logout } = useAdmin();
  const [tab, setTab] = useState("placed");
  const [q, setQ] = useState("");
  const [data, setData] = useState({ orders: [], counts: {} });
  const [restock, setRestock] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    adminApi.get("/admin/orders", { params: { status: tab || undefined, q: q || undefined } }).then((r) => setData(r.data)).catch((e) => toast.error(formatErr(e)));
  }, [tab, q]);
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);
  useEffect(() => { adminApi.get("/admin/restock-requests").then((r) => setRestock(r.data)); }, []);

  const act = async (o, action) => {
    setBusy(true);
    try {
      await adminApi.post(`/admin/orders/${o.order_number}/status`, { action });
      toast.success({ confirm: "Order confirmed", reject: "Marked as payment not received", ship: "Marked as shipped" }[action]);
      load();
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  const total = Object.values(data.counts).reduce((a, b) => a + b, 0);

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#FAF7F2]">
      <header className="sticky top-0 z-30 border-b border-[#E7E2DB] bg-[#FAF7F2]/90 backdrop-blur-md">
        <div className="kt-container flex h-16 items-center justify-between">
          <div className="flex items-center gap-4"><Logo /><span className="hidden rounded-full bg-[#701A24] px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-white sm:inline">Admin</span></div>
          <div className="flex items-center gap-3 text-sm">
            <span className="hidden text-[#78716C] sm:inline">{user?.email}</span>
            <Link to="/" className="text-[#B85D43]">View store</Link>
            <button data-testid="admin-logout-button" onClick={logout} className="flex items-center gap-1 rounded-full border border-[#E7E2DB] px-3 py-1.5"><LogOut className="h-3.5 w-3.5" /> Logout</button>
          </div>
        </div>
      </header>
      <main className="kt-container py-8">
        <h1 className="font-serif-display text-4xl font-semibold">Orders</h1>
        <p className="mt-1 text-sm text-[#78716C]">Match each UTR with the payment in your Paytm for Business app, then confirm. List refreshes every 30 seconds.</p>
        <div className="no-scrollbar mt-6 flex gap-2 overflow-x-auto pb-1">
          {TABS.map(([v, l]) => (
            <button key={v || "all"} data-testid={`admin-tab-${v || "all"}`} onClick={() => setTab(v)} className={`shrink-0 rounded-full border px-4 py-2 text-sm ${tab === v ? "border-[#701A24] bg-[#701A24] text-white" : "border-[#E7E2DB] bg-white"}`}>
              {l} <span className="ml-1 opacity-70">{v ? data.counts[v] || 0 : total}</span>
            </button>
          ))}
        </div>
        <div className="mt-4 flex gap-2">
          <label className="flex flex-1 items-center gap-2 rounded-full border border-[#E7E2DB] bg-white px-4">
            <Search className="h-4 w-4 text-[#A8A29E]" />
            <input data-testid="admin-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search order no., UTR, phone or name" className="min-w-0 flex-1 bg-transparent py-2.5 text-sm outline-none" />
          </label>
          <button data-testid="admin-refresh-button" onClick={load} aria-label="Refresh" className="rounded-full border border-[#E7E2DB] bg-white p-3"><RefreshCw className="h-4 w-4" /></button>
        </div>
        <div className="mt-6 grid gap-8 lg:grid-cols-12">
          <div data-testid="admin-orders-list" className="space-y-4 lg:col-span-8">
            {data.orders.length === 0 ? <p className="rounded-2xl border border-dashed border-[#E7E2DB] p-10 text-center text-sm text-[#78716C]">No orders here.</p> : data.orders.map((o) => <OrderCard key={o.order_number} o={o} onAction={act} busy={busy} />)}
          </div>
          <aside className="lg:col-span-4">
            <div data-testid="admin-restock-panel" className="rounded-2xl border border-[#E7E2DB] bg-white p-5">
              <p className="font-serif-display text-2xl font-semibold">Restock requests</p>
              {restock.length === 0 ? <p className="mt-2 text-sm text-[#78716C]">No requests yet.</p> : (
                <ul className="mt-3 space-y-2 text-sm">
                  {restock.map((r) => <li key={r.slug + r.size} className="flex justify-between gap-2"><Link to={`/products/${r.slug}`} className="line-clamp-1 hover:text-[#701A24]">{r.slug.replace(/-/g, " ")} · <b>{r.size}</b></Link><span className="shrink-0 rounded-full bg-[#F4E4DE] px-2 text-xs font-semibold text-[#701A24]">{r.requests}</span></li>)}
                </ul>
              )}
            </div>
          </aside>
        </div>
      </main>
    </div>
  );
}
